import { createRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { ProtectedRoute } from "../auth/ProtectedRoute";
import { useAuth } from "../auth/AuthContext";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { rootRoute } from "./__root";

interface TerritoryPayload {
  territories: Array<{
    id: number;
    code: string;
    label: string;
    is_active: boolean;
    display_order: number;
    zip_codes: string[];
  }>;
}

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings/territories",
  component: TerritoriesScreen,
});

function TerritoriesScreen() {
  return (
    <ProtectedRoute requiredRoles={["owner"]}>
      <TerritoriesContent />
    </ProtectedRoute>
  );
}

function TerritoriesContent() {
  const { accessToken } = useAuth();
  const [data, setData] = useState<TerritoryPayload | null>(null);

  useEffect(() => {
    if (!accessToken) return;
    fetch("/api/cockpit/settings/territories", {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: "include",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then(setData)
      .catch(() => setData(null));
  }, [accessToken]);

  return (
    <section className="space-y-5">
      <div>
        <Badge variant="slate">Settings</Badge>
        <h1 className="mt-3 text-2xl font-semibold text-slate-950">Territories</h1>
        <p className="mt-1 text-sm text-slate-600">Review tenant territory codes and assigned ZIP codes.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {(data?.territories || []).map((territory) => (
          <Card key={territory.id} className={territory.is_active ? "" : "opacity-60"}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{territory.label}</span>
                <Badge variant="slate">{territory.code}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {territory.zip_codes.length > 0 ? territory.zip_codes.map((zip) => (
                  <span key={zip} className="rounded border px-2 py-1 text-xs text-slate-700">{zip}</span>
                )) : <span className="text-sm text-slate-500">No ZIP codes assigned.</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
